import { Image, StyleSheet } from 'react-native'

import { DS } from '../../../../design-system'
import { LiveBadge } from '../TodayLiveGames/LiveBadge'
import pattern1 from './assets/pattern1.png'
import pattern2 from './assets/pattern2.png'
import pattern3 from './assets/pattern3.png'
import pattern4 from './assets/pattern4.png'
import { Doc } from '@ppwr/convex/_generated/dataModel'
import { monthDayAndTimeFormatter } from '../../../../utils'
import { capitalizeFirstLetter } from '../../../../utils/formatString'

const patterns = [pattern1, pattern2, pattern3, pattern4]

// `seats` prop:
// - if practice game, use array of 1 number: the number of bots
// - if regular game, use array of 2 numbers: [current players, max players]

interface YourGameCardPropsCommon {
  type?: 'live' | 'upcoming' | 'finished'
  iAmHost?: boolean
  tableName?: string
  blinds?: readonly number[]
  seats?: readonly number[]
  isFull?: boolean
  isPractice?: boolean
  label?: string
  hasLeftGame?: boolean
  isLocked?: boolean
  pattern?: number
  scheduledAt?: Date
  onPress?: () => void
}

interface YourGameCardPropsLegacy extends YourGameCardPropsCommon {
  context: 'legacy' | undefined
  gameMode?: never
  gameState?: never
  channelName?: string
}
interface YourGameCardPropsConvex extends YourGameCardPropsCommon {
  context: 'convex'
  gameMode?: Doc<'games'>['mode']
  gameState?: Doc<'games'>['state']
  channelName?: never
}
type YourGameCardProps = YourGameCardPropsLegacy | YourGameCardPropsConvex

export const YourGameCard: React.FC<YourGameCardProps> = ({
  context,
  type,
  iAmHost,
  blinds = [50, 100],
  seats = [4, 9],
  channelName,
  hasLeftGame,
  isLocked,
  isFull,
  isPractice = false,
  gameMode,
  gameState,
  onPress,
  scheduledAt,
  pattern = 0,
  tableName
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}) => {
  const isLive = 'convex' === context ? 'playing' === gameState : type === 'live'
  const isObserve = isFull || isLocked || hasLeftGame
  const buttonText = isLive ? (isObserve ? 'Observe' : 'Join') : iAmHost ? 'Manage' : 'View'
  let seatsText = isFull ? 'Table Full' : `${seats[1] - seats[0]} seats available`
  const statusText = `${seats[0]}/${seats[1]} • ${seatsText}`
  const patternImage = patterns[pattern % patterns.length]
  const hasBots = (isPractice || gameMode === 'practice') && seats?.length === 1
  if (hasBots) seatsText = `${seats[0]} bots`

  const modeText =
    'convex' === context ? capitalizeFirstLetter(gameMode || 'unknown') : isPractice ? 'Practice' : 'Tournament'

  return (
    <DS.Frame bg="Global/White" style={styles.container}>
      <DS.Frame style={styles.body}>
        <DS.Frame hidden={!isLive} style={styles.liveBadge}>
          <LiveBadge isPractice={isPractice} />
        </DS.Frame>
        <DS.Frame gap="16">
          <DS.Frame gap="8">
            <DS.Text variant="Body Small/Regular">{modeText}</DS.Text>
            <DS.Text variant="Heading 4/Medium" numberOfLines={1}>
              {tableName}
            </DS.Text>
            {/* SHOWING WHEN LIVE */}
            <DS.Frame gap="8" hidden={!isLive}>
              {blinds.length === 2 && (
                <DS.Frame>
                  <DS.Text variant="Body Small/Regular">
                    Blinds {blinds[0]}/{blinds[1]}
                  </DS.Text>
                </DS.Frame>
              )}
              <DS.Frame gap="6" row>
                <DS.Icon name={hasBots ? 'YinYang' : 'Person'} color="Brand/Orange" size={16} />
                <DS.Text variant="Body Small/Regular">{seatsText}</DS.Text>
              </DS.Frame>
            </DS.Frame>
            {/* SHOWING WHEN SCHEDULE */}
            <DS.Frame gap="8" hidden={isLive}>
              <DS.Frame row>
                {scheduledAt ? (
                  <DS.Text variant="Body Small/Regular">{monthDayAndTimeFormatter(scheduledAt)}</DS.Text>
                ) : null}
              </DS.Frame>
              <DS.Frame row hidden={!iAmHost}>
                <DS.Icon name="Person--filled" color="Global/Black" size={16} />
                <DS.Text variant="Body Small/Regular">You’re host</DS.Text>
              </DS.Frame>
            </DS.Frame>
          </DS.Frame>
        </DS.Frame>
        <DS.Button
          variant={isObserve ? 'Medium/Secondary' : 'Medium/Primary'}
          type={isObserve ? 'On Dark' : 'On Light'}
          onPress={async () => {
            if (onPress) {
              await onPress()
            }
          }}
        >
          {buttonText}
        </DS.Button>
      </DS.Frame>
      <Image source={patternImage} style={styles.pattern} />
    </DS.Frame>
  )
}

const styles = StyleSheet.create({
  container: { borderRadius: DS.spacing[16], height: 227, width: 204 },
  body: {
    borderTopRightRadius: DS.spacing[16],
    borderTopLeftRadius: DS.spacing[16],
    padding: DS.spacing[16],
    justifyContent: 'space-between',
    height: 211
  },
  liveBadge: {
    position: 'absolute',
    top: 0,
    right: DS.spacing[16]
  },
  pattern: {
    width: '100%',
    height: 16,
    borderBottomLeftRadius: DS.spacing[16],
    borderBottomRightRadius: DS.spacing[16]
  }
})
